import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WordCloud, type WordCloudWord } from '../../../shared/ui/components/WordCloud';
import { useTagCloudData } from '../hooks/useTagCloudData';
import { useSearch } from '../../../shared/contexts/SearchContext';

interface TagWordCloudProps {
  onTagSelect?: (tagId: string, tagName: string) => void;
  selectedTags?: string[];
  className?: string;
}

export function TagWordCloud({
  onTagSelect,
  selectedTags = [],
  className = '',
}: TagWordCloudProps) {
  const { data, isLoading } = useTagCloudData();
  const navigate = useNavigate();
  const { filterDispatch } = useSearch();

  // Memoize the word transformation to prevent unnecessary recalculations
  const words: WordCloudWord[] = useMemo(() => {
    if (!data?.nodes) return [];
    
    return data.nodes.map((node) => ({
      text: node.data.name,
      size: node.data.count,
      id: node.id,
      count: node.data.count,
    }));
  }, [data?.nodes]);

  // Memoize the click handler to prevent recreation
  const handleWordClick = useCallback((word: WordCloudWord) => {
    if (word.id && word.text) {
      // Add the tag to the filter state using the correct action type
      const tagId = `inclusive-${word.text}`;
      filterDispatch({
        type: 'TOGGLE_TAG_MODE',
        tagId,
        value: word.text,
        category: undefined
      });

      // Navigate to the main search page to show filtered results
      void navigate('/');

      // Call the optional callback if provided
      if (onTagSelect) {
        onTagSelect(word.id, word.text);
      }
    }
  }, [onTagSelect, filterDispatch, navigate]);

  const handleNavigateToNewReference = useCallback(() => {
    void navigate('/new-reference');
  }, [navigate]);

  const handleNavigateToHome = useCallback(() => {
    void navigate('/');
  }, [navigate]);

  if (isLoading) {
    return (
      <div className={`w-full space-y-8 ${className}`}>
        {/* Pop Art Loading Header */}
        <div className="comic-panel p-8 bg-gradient-to-r from-yellow-300 to-blue-300 bg-ben-day-light text-center">
          <div className="spinner-pop mx-auto mb-6" />
          <h2 className="text-pop-extra-bold text-3xl mb-4">LOADING TAG CLOUD...</h2>
          <p className="text-pop-bold text-lg">Getting all your amazing tags ready!</p>
          <div className="flex justify-center items-center gap-3 mt-4">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i}
                className="w-4 h-4 bg-purple-500 border-pop-thin shadow-pop-sm animate-bounce"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        </div>

        {/* Loading word cloud placeholder */}
        <div className="comic-panel bg-gradient-to-br from-white via-yellow-50 to-blue-50 min-h-[600px] flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse">🏷️</div>
            <h3 className="text-pop-bold text-2xl mb-4 animate-pulse">PREPARING WORD CLOUD...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.nodes.length === 0) {
    return (
      <div className={`w-full space-y-8 ${className}`}>
        {/* Pop Art Empty State Header */}
        <div className="comic-panel p-8 bg-gradient-to-r from-yellow-300 to-red-300 bg-ben-day-light text-center">
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto border-pop-thick bg-white rounded-full flex items-center justify-center shadow-pop-xl">
              <svg className="w-12 h-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
          </div>
          <h2 className="text-pop-extra-bold text-4xl mb-4">NO TAGS YET!</h2>
          <p className="text-pop-bold text-xl mb-8">Add some references with tags to see your EPIC word cloud</p>
          <div className="text-comic-pow text-2xl">POW!</div>
        </div>

        {/* Empty state word cloud */}
        <div className="comic-panel bg-gradient-to-br from-white via-yellow-50 to-blue-50 min-h-[600px] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-8xl mb-6">🌟</div>
            <h3 className="text-pop-extra-bold text-3xl mb-6">CREATE YOUR FIRST TAG!</h3>
            <p className="text-lg mb-8 font-semibold text-gray-700">
              Tags help you organize and find your references. Start by adding your first reference!
            </p>
            <button
              onClick={handleNavigateToNewReference}
              className="btn btn-primary btn-lg text-comic-pow shadow-pop-lg border-pop-thick"
            >
              <span className="text-comic-pow text-xl">ADD REFERENCE!</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full space-y-8 ${className}`}>
      {/* Enhanced Pop Art Statistics Header */}
      <div className="comic-panel p-8 bg-gradient-to-r from-yellow-300 via-green-300 to-blue-300 bg-ben-day-light">
        <div className="flex flex-wrap items-center justify-center gap-8">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-blue-500 border-pop-thick shadow-pop-lg rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <div className="text-center">
              <div className="text-pop-extra-bold text-2xl">{words.length}</div>
              <div className="text-pop-bold text-sm">UNIQUE TAGS</div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-green-500 border-pop-thick shadow-pop-lg rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">R</span>
            </div>
            <div className="text-center">
              <div className="text-pop-extra-bold text-2xl">{data?.totalReferences || 0}</div>
              <div className="text-pop-bold text-sm">REFERENCES</div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-purple-500 border-pop-thick shadow-pop-lg rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">✨</span>
            </div>
            <div className="text-center">
              <div className="text-pop-extra-bold text-2xl">∞</div>
              <div className="text-pop-bold text-sm">POSSIBILITIES</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Full-Width Responsive Word Cloud */}
      <div className="w-full">
        <WordCloud
          words={words}
          height={700}
          responsive={true}
          className="w-full shadow-pop-xl border-pop-thick"
          onWordClick={handleWordClick}
          selectedWords={selectedTags}
          maxFontSize={64}
          minFontSize={18}
        />
      </div>

      {/* Enhanced Pop Art Action Footer */}
      <div className="comic-panel p-8 bg-gradient-to-r from-green-300 via-purple-300 to-pink-300 bg-ben-day-medium">
        <div className="text-center space-y-6">
          <div className="mb-6">
            <h3 className="text-pop-extra-bold text-3xl mb-4">READY TO EXPLORE?</h3>
            <p className="text-pop-bold text-xl">
              Click any tag above to filter your references and dive deep into your collection!
            </p>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-6">
            <button
              onClick={handleNavigateToHome}
              className="btn btn-outline btn-lg shadow-pop-lg border-pop-thick group hover:scale-100 transition-transform"
            >
              <span className="text-comic-bam text-xl group-hover:text-comic-zoom">BROWSE ALL!</span>
            </button>
            
            <div className="text-4xl animate-bounce">⚡</div>
            
            <button
              onClick={handleNavigateToNewReference}
              className="btn btn-primary btn-lg shadow-pop-lg border-pop-thick group hover:scale-105 transition-transform"
            >
              <span className="text-comic-zoom text-xl group-hover:text-comic-pow">ADD MORE!</span>
            </button>
          </div>
          
          <div className="mt-6 flex justify-center items-center gap-2">
            <span className="text-pop-bold text-lg">Tip:</span>
            <span className="comic-panel bg-white px-3 py-1 border-pop-thin text-sm font-bold">
              Larger tags = more references!
            </span>
          </div>
        </div>
      </div>
    </div>
  );
} 